import { makeAutoObservable } from "mobx";
import { FormSubmitMethod } from "helpers/decorators/formSubmitMethod";
import { directorFormToDirectorCreateData, directorFormToDirectorUpdateData } from "helpers/forms/au/director";
import type { DirectorFormCommonValues, DirectorStepFormValues } from "helpers/forms/au/directorTypes";
import type { CompanyStore } from "services/company/companyStore";
import { DirectorAddressStore } from "services/director/directorAddressStore";
import { type DirectorStore } from "services/director/directorStore";
import { DirectorPositionStore } from "services/directorPosition/directorPositionStore";
import { FormError } from "types/errors";
import { type FormSubmitResult } from "types/form";

export enum DirectorFormStep {
  Position = "Position",
  NameData = "NameData",
  FormerNameCheck = "FormerNameCheck",
  FormerName = "FormerName",
  BirthDetails = "BirthDetails",
  Address = "Address",
  ContactDetails = "ContactDetails",
}

const DIRECTOR_STEPS = [
  DirectorFormStep.Position,
  DirectorFormStep.NameData,
  DirectorFormStep.FormerNameCheck,
  DirectorFormStep.FormerName,
  DirectorFormStep.BirthDetails,
  DirectorFormStep.Address,
  DirectorFormStep.ContactDetails,
];

const SHAREHOLDER_STEPS = [
  DirectorFormStep.Position,
  DirectorFormStep.NameData,
  DirectorFormStep.BirthDetails,
  DirectorFormStep.Address,
  DirectorFormStep.ContactDetails,
];

const LOGICAL_STEPS = [DirectorFormStep.FormerName];

export class DirectorFormStore {
  private _activeStep: DirectorFormStep = DirectorFormStep.Position;
  private readonly _editMode: boolean;
  private _activeDirector: DirectorFormCommonValues | null = null;

  constructor(
    private readonly _directorStore: DirectorStore,
    private readonly _companyStore: CompanyStore,
    private readonly _addressStore: DirectorAddressStore,
    private readonly _directorPositionsStore: DirectorPositionStore,
    private _directorId?: string,
  ) {
    this._editMode = !!_directorId;

    makeAutoObservable(this);
  }

  get activeStep(): DirectorFormStep {
    return this._activeStep;
  }

  set activeStep(value: DirectorFormStep) {
    this._activeStep = value;
  }

  get isFirstStep(): boolean {
    return this.activeStep === DIRECTOR_STEPS[0];
  }

  get isLastStep(): boolean {
    if (this.activeDirector?.isDirector) {
      return this.activeStep === DIRECTOR_STEPS.at(-1);
    }
    // for shareholders, the last step is the Address
    return this.activeStep === SHAREHOLDER_STEPS.at(-1);
  }

  get editMode(): boolean {
    return this._editMode;
  }

  get activeDirector(): DirectorStepFormValues | null {
    return this._activeDirector;
  }

  set activeDirector(value: DirectorStepFormValues | null) {
    // set default values
    this.setDefaultStepValues(value);
    this._activeDirector = value;
  }

  setDefaultStepValues(value: DirectorStepFormValues | null): void {
    if (!value) {
      return;
    }

    if (value.formerFirstName || value.formerMiddleNames || value.formerLastName) {
      value.formerNameCheck = true;
    }
  }

  getDirectorId(): string {
    if (!this._directorId) {
      throw new FormError("No director's id");
    }

    return this._directorId;
  }

  getDirectorFirstName(): string {
    if (!this._activeDirector) {
      throw new FormError("No active director found!");
    }
    return this._activeDirector.firstName || "";
  }

  @FormSubmitMethod()
  async onSubmit(values: DirectorStepFormValues): Promise<FormSubmitResult> {
    switch (this.activeStep) {
      case DirectorFormStep.Position:
      case DirectorFormStep.FormerNameCheck:
      case DirectorFormStep.FormerName:
      case DirectorFormStep.BirthDetails:
      case DirectorFormStep.NameData:
        break;
      case DirectorFormStep.Address:
        // if the director is not a shareholder, update common data and address
        if (!values.isDirector && values.isShareholder) {
          await this._updateCommonData(values);
          await this._updateAddress(values);
          // set the active step to ContactDetails to skip form
          this.activeStep = DirectorFormStep.ContactDetails;
        }
        break;
      case DirectorFormStep.ContactDetails:
        await this._updateCommonData(values);
        await this._updateAddress(values);
        break;
    }

    if (!this.isLastStep) {
      this._goForward();
    }
  }

  goBack(): void {
    const STEPS = this.activeDirector?.isDirector ? DIRECTOR_STEPS : SHAREHOLDER_STEPS;
    let activeStep = STEPS[STEPS.indexOf(this.activeStep) - 1] as unknown as DirectorFormStep;
    // check if the active step is in logical steps
    if (LOGICAL_STEPS.includes(activeStep)) {
      // skip logical steps
      activeStep = STEPS[STEPS.indexOf(activeStep) - 1] as unknown as DirectorFormStep;
    }
    this.activeStep = activeStep;
  }

  goForward(): void {
    this._goForward();
  }

  private _goForward(): void {
    const STEPS = this.activeDirector?.isDirector ? DIRECTOR_STEPS : SHAREHOLDER_STEPS;
    this.activeStep = STEPS[STEPS.indexOf(this.activeStep) + 1] as unknown as DirectorFormStep;
  }

  private async _updateCommonData(values: DirectorFormCommonValues): Promise<void> {
    if (!values.position) {
      throw new FormError("Something went wrong, no director's position in the form");
    }

    if (this._directorId) {
      await this._directorStore.updateDirector(this._directorId, directorFormToDirectorUpdateData(values));
    } else {
      this._directorId = await this._directorStore.createDirector(
        directorFormToDirectorCreateData(this._companyStore.getCompany().id, values),
      );
    }

    this._directorPositionsStore.setForDirector(this._directorId, values.position);
  }

  private async _updateAddress(values: DirectorStepFormValues): Promise<void> {
    if (!values.address) {
      throw new FormError("Something went wrong, no address in the form");
    }

    if (values.isAustralianCountry === false) {
      values.address.postcode = "";
    }

    await this._addressStore.updateForDirector(this.getDirectorId(), values.address);
  }
}
